import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Tooltip,
  Chip,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

function TopMovers({ collections }) {
  const navigate = useNavigate();
  
  // Get collections for the current 24-hour period
  const currentCollections = useMemo(() => {
    // Get the current date at midnight to use as a seed
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysSinceEpoch = Math.floor(today.getTime() / (1000 * 60 * 60 * 24));
    
    // Use the days since epoch to determine which collections to show
    const collectionCount = collections.length;
    const startIndex = (daysSinceEpoch * 4) % collectionCount;
    
    // Get 4 collections starting from the calculated index, wrapping around if needed
    let selectedCollections = [];
    for (let i = 0; i < 4; i++) {
      const index = (startIndex + i) % collectionCount;
      selectedCollections.push(collections[index]);
    }
    
    // Sort the selected collections by price change
    return selectedCollections.sort((a, b) => Math.abs(b.priceChange) - Math.abs(a.priceChange));
  }, [collections]);

  // Calculate time until next rotation
  const timeUntilNextRotation = useMemo(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.getTime() - now.getTime();
  }, []);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          Top Movers (24h)
        </Typography>
        <Tooltip title={`Next rotation in ${Math.floor(timeUntilNextRotation / (1000 * 60 * 60))} hours`} arrow>
          <Chip 
            label="Rotating Daily" 
            size="small"
            color="primary"
            sx={{ 
              height: '24px',
              '& .MuiChip-label': {
                fontSize: '0.75rem',
              }
            }}
          />
        </Tooltip>
      </Box>
      <Grid container spacing={2}>
        {currentCollections.map((collection) => (
          <Grid item xs={12} sm={6} md={3} key={collection.id}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: 'background.paper',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => `0 4px 20px ${theme.palette.primary.main}25`,
                },
              }}
            >
              <CardActionArea onClick={() => navigate(`/collection/${collection.id}`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      component="img"
                      src={collection.image}
                      alt={collection.name}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        mr: 1,
                      }}
                    />
                    <Typography variant="subtitle1" noWrap>
                      {collection.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tooltip title="Floor Price" arrow>
                      <Typography variant="body2" color="text.secondary">
                        {collection.floorPrice} ETH
                      </Typography>
                    </Tooltip>
                    <Tooltip title="24h Change" arrow>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {collection.priceChange > 0 ? (
                          <TrendingUpIcon sx={{ color: '#4CAF50', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ color: '#FF2E63', mr: 0.5 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            color: collection.priceChange > 0 ? '#4CAF50' : '#FF2E63',
                            fontWeight: 'bold',
                          }}
                        >
                          {Math.abs(collection.priceChange).toFixed(2)}%
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default TopMovers;
